import { workingDaysBetween } from "./schedule/calendar.js";

/* Capacity and utilization, the honest way.
 *
 * Utilization is hours against hours *available*, and available is not a flat
 * forty a week. It is the working days in the period, times a standard day,
 * minus the company holidays that land in it, minus whatever time off the
 * person has booked. Get that denominator wrong and every holiday week reads
 * as slack that is not there.
 *
 * Everything here is pure. It takes the holiday list the app already holds and
 * the hours already recorded, and returns numbers. No fetching, no component
 * state, so the same arithmetic runs in a cell, a rollup and a test.
 */

export const STANDARD_DAY_HOURS = 8;

/* The company holiday hours that fall within a period, inclusive. A holiday on
   a weekend still counts its hours only if the firm books it that way; the
   stored hours are the authority, so a half day reads as four. */
export function holidayHoursInRange(holidays = [], startIso, endIso) {
  if (!startIso || !endIso || endIso < startIso) return 0;
  return holidays.reduce((sum, h) => {
    const d = h.holiday_date;
    if (d && d >= startIso && d <= endIso) {
      const hrs = Number(h.hours);
      return sum + (Number.isFinite(hrs) ? hrs : STANDARD_DAY_HOURS);
    }
    return sum;
  }, 0);
}

/* Hours a person could bill in a period: the working days times a standard
   day, less the company holidays in it, less their own time off. Never below
   zero, because a week that is all holiday and PTO has no capacity, not
   negative capacity. */
export function availableHours({
  start, end, holidays = [], timeOffHours = 0, dayHours = STANDARD_DAY_HOURS,
} = {}) {
  const workdays = workingDaysBetween(start, end);
  if (workdays === null) return null;
  const gross = workdays * dayHours;
  const holiday = holidayHoursInRange(holidays, start, end);
  const off = Number.isFinite(Number(timeOffHours)) ? Number(timeOffHours) : 0;
  return Math.max(0, gross - holiday - off);
}

/* Utilization as a percentage, assigned against available. Null when there is
   no available time to divide by, because a person with zero capacity has no
   utilization rather than an infinite one. Over 100 is kept, not clamped: that
   is the overallocation a leader needs to see. */
export function utilization({ assignedHours = 0, available = null } = {}) {
  if (available === null || available <= 0) return null;
  return Math.round((Number(assignedHours) / available) * 100);
}

/* Full-time-equivalent for a block of hours over a period, against a standard
   working week. Three weeks at forty hours is 1.0 FTE; sixty is 1.5. */
export function fteForPeriod({ assignedHours = 0, start, end, weeklyStandard = 40 } = {}) {
  const workdays = workingDaysBetween(start, end);
  if (!workdays) return null;
  const weeks = workdays / 5;
  const capacity = weeks * weeklyStandard;
  return capacity > 0 ? Math.round((Number(assignedHours) / capacity) * 100) / 100 : null;
}

/* The band a leader reads at a glance. Under-booked, healthy, stretched, over.
   The thresholds are the ones a staffing conversation actually uses: a person
   under half is bench, over 100 is a problem, and the wide middle is fine. */
export function utilizationBand(pct) {
  if (pct === null || pct === undefined) return { key: "unknown", label: "no capacity" };
  if (pct > 100) return { key: "over", label: "over capacity" };
  if (pct >= 85) return { key: "high", label: "fully booked" };
  if (pct >= 50) return { key: "ok", label: "healthy" };
  return { key: "low", label: "under-booked" };
}
